import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getCustomRecords } from '../utils/urls';

import { ErrorModal, SortItemType } from 'types/modalTypes';
import { CustomRecord } from 'types/customTables';

const pageLength = 100;
function getColumnFromId(columns: Array<string>, id: string) {
  const idsList = columns;
  const index = idsList.findIndex(item => item === id);
  return `${index}`;
}

async function fetchCustomRecord({
  requestData,
  accountId,
  tableId,
  pageParam,
}: TVariables): Promise<TResult> {
  if (tableId === 0) return;
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getCustomRecords(accountId, tableId), {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchCustomRecord, 500);

type TError = ErrorModal;

type TResult =
  | {
      Data: Array<CustomRecord>;
      Draw: number;
      FilteredRecords: number;
      IsAutoRefreshEnabled: boolean;
      IsSuccess: boolean;
      PageNumber: number;
      StatusCode: number;
      TotalRecords: number;
    }
  | undefined;

type TVariables = {
  requestData: any;
  accountId: number;
  tableId: number;
  pageParam: number;
};

function formatRequestData(
  columnsSearch: Record<string, string>,
  sortedColumn: string,
  sortOrder: string,
  keywordSearch: string,
  columns: Array<string>,
): FormData {
  const formData: any = new URLSearchParams();
  formData.append('draw', '1');
  columns.forEach((item: any, index: number) => {
    formData.append(`columns[${index}][data]`, 'function');
    formData.append(`columns[${index}][name]`, '');
    formData.append(`columns[${index}][searchable]`, 'true');
    formData.append(`columns[${index}][orderable]`, 'true');
    formData.append(
      `columns[${index}][search][value]`,
      columnsSearch && columnsSearch[item] ? columnsSearch[item] : '',
    );
    formData.append(`columns[${index}][search][regex]`, 'false');
  });
  formData.append('order[0][column]', sortedColumn);
  formData.append('order[0][dir]', sortOrder);
  formData.append('search[value]', `${keywordSearch}`);
  formData.append('search[regex]', 'false');
  formData.append('length', `${100}`);
  formData.append('timezoneOffset', '-330');
  return formData;
}

function useGetCustomRecords(
  columnsSearch: Record<string, string>,
  sortItem: SortItemType,
  keywordSearch: string,
  accountId: number,
  tableId: number,
  columns: Array<string>,
): UseInfiniteQueryResult<TResult, TError> {
  const requestData = formatRequestData(
    columnsSearch,
    getColumnFromId(columns, sortItem.sortKey),
    sortItem.sortOrder,
    keywordSearch,
    columns,
  );

  return useInfiniteQuery(
    [
      'custom_records',
      keywordSearch,
      columnsSearch,
      sortItem,
      accountId,
      tableId,
    ],
    ({ pageParam = 0 }) =>
      debouncedFetch({ requestData, accountId, tableId, pageParam }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetCustomRecords };
